// Libraries
import React, { useEffect, useRef, useState } from 'react'

// Webinar Page Modules
import Hero from '../../../global/hero'
import SEOTags from '../../../global/seoTags'

// Helpers
import { UseCases } from '../../../../constants/useCases.constants'
import { kebabCaseString } from '../../../../utils/functions.utils'
import ImgUtil from '../../../../utils/img.utils'
import { internalLinkHandler } from '../../../../utils/link.utils'
import { findTarget } from '../../../../utils/microcopy.utils'
import RandomizerUtil from '../../../../utils/randomizer.utils'
import UrlUtil from '../../../../utils/url.utils'
import { ResourceCard } from './resourceCard'

// Constants
import { ModuleNames } from '../../../../constants/componentNames.constants'
import { LocaleCodes } from '../../../../constants/localization.constants'

// Styles
import {
  Checkbox,
  FilterGroup,
  InputContainer,
  MoreResourcesContainer,
  ResourceListingBottomContainer,
  ResourceListingContainer,
  ResourceListingItemContainer,
  ResourceListingLeftFilterContainer,
  ResourceListingTopFilterContainer,
  ResourcePageOuter,
  SearchContainer,
  SearchIconContainer,
  SearchResultsDescription,
  ToggleText,
  TrendingResourcesContainer,
} from './style'

import { PaginationContainer } from '../../partners/partnerListings'

// Componenets
import Button from '../../../global/button'
import ContentBlock from '../../../global/contentBlock'
import GlobalContainer from '../../../global/globalContainer'
import Pagination from '../../../global/pagination'
import ResourceRow from '../../../global/resourceRow'
import Typography from '../../../global/typography'
import WrapperImage from '../../../global/wrapperImage'
import Layout from '../../../layouts/layout'
import DropDown from './dropdown'

const BlueArrowDown = ({ isEvents }: boolean) => {
  return (
    <svg
      width="9"
      height="5"
      viewBox="0 0 9 5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="Filter-Group__Arrow">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.213619 0.229708C-0.0465667 0.492817 -0.0441968 0.917031 0.218912 1.17722L3.56824 4.48933C3.62343 4.54408 3.6858 4.58727 3.75233 4.61889C3.93396 4.70522 4.14627 4.7051 4.3277 4.61889C4.39424 4.58727 4.45661 4.54408 4.5118 4.48933L7.86113 1.17722C8.12424 0.917031 8.12661 0.492817 7.86642 0.229708C7.60623 -0.0334005 7.18202 -0.0357699 6.91891 0.224415L4.04002 3.07132L1.16113 0.224415C0.898018 -0.0357699 0.473804 -0.0334005 0.213619 0.229708Z"
        fill={isEvents ? '#38c6f4' : '#0067DF'}
      />
    </svg>
  )
}

const RedX = () => {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.99 5.99999L11.495 1.49496C11.7684 1.2216 11.7684 0.77838 11.495 0.505013C11.2217 0.231646 10.7784 0.231646 10.5051 0.505013L6.00005 5.01004L1.49502 0.505013C1.22166 0.231646 0.778442 0.231646 0.505075 0.505013C0.231708 0.77838 0.231708 1.2216 0.505075 1.49496L5.0101 5.99999L0.505074 10.505C0.231707 10.7784 0.231707 11.2216 0.505074 11.495C0.778441 11.7683 1.22166 11.7683 1.49502 11.495L6.00005 6.98994L10.5051 11.495C10.7784 11.7683 11.2217 11.7683 11.495 11.495C11.7684 11.2216 11.7684 10.7784 11.495 10.505L6.99 5.99999Z"
        fill="#FA4616"
      />
    </svg>
  )
}

const ResourceCenterLandingPage = ({
  pageContext,
  location,
  path,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}: any) => {
  const pageData = pageContext.listingPageData ? pageContext.listingPageData : '',
    resourceData = pageContext.resourceData ? pageContext.resourceData : [],
    microcopy = pageData.microcopy ? pageData.microcopy : [],
    featuredResources = pageContext.featuredResources ? pageContext.featuredResources : []

  const useCase = pageData?.useCase

  const centerTabs: any = pageContext.listingTabs?.pageTabs || []

  // For localized page, used to go to en listing page
  let wantMore = findTarget('WantMore', microcopy.values, false)
  let allResources = findTarget('AllResources', microcopy.values, false)
  const isEvents = useCase === UseCases.RESOURCE_LISTING_EVENTS ? true : false
  const seoData = pageData && pageData.seoMetadata ? pageData.seoMetadata : null
  const filterDrawers = useRef<any>({})
  let eventPageContentBlock: any = '',
    eventPageResourceRow: any = '',
    eventsContentBlockHeadline: string = ''

  let dynamicPageContent: any
  let defaultLocale: string
  let defaultSlug: string

  if (pageData && pageData.slug && pageData.slug.charAt(0) === '/') {
    defaultSlug = pageData.slug.trim()
  } else {
    defaultSlug = `/${pageData.slug.trim()}`
  }

  if (pageContext && pageContext.pageLocale) {
    defaultLocale = pageContext.pageLocale
  } else {
    defaultLocale = LocaleCodes.ENGLISH.code
  }

  const pageNumber = pageContext.pageNumber ? pageContext.pageNumber : ''

  // Filtering
  // used to store all topic filters
  const [queryStringFilterSet, setQueryStringFilterSet] = useState<any>(null)
  // combines queryStringFilterSet and search param, easier to add search param to end or
  const [queryString, setQueryString] = useState<any>(null)

  const [checkboxFilters, setCheckboxFilters] = useState<any>([])
  const [filtersAdded, setFiltersAdded] = useState<any>([])
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false)
  const [listingData, setListingData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>()

  // Pagination
  const resourcesPerPage = 12

  // Only used internally not by the paging system
  const handlePaginationClick = (page: number, isFilter?: boolean) => {
    let urlQuery = typeof window !== 'undefined' ? window.location.search : ''
    if (page > 1) {
      history.pushState(null, '', `${defaultSlug}/${page}${urlQuery}`)
    } else {
      history.pushState(null, '', `${defaultSlug}${urlQuery}`)
    }
    // If mobile drawer is open, this sends you to a weird position
    if (typeof document !== undefined && isFilter && !mobileDrawerOpen) {
      document
        .getElementById('ResourceListingTopFilterContainer')
        .scrollIntoView({ behavior: 'smooth' })
    } else if (typeof window !== undefined) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const urlFormatSearchKeyword = (searchKeyword: string) => {
    return searchKeyword.split('/').join('_').replace(/ /g, '-')
  }

  const handleSearchInput = (e: any) => {
    setSearchText(e.target.value)
    if (e.target.value.length === 0) {
      history.pushState(null, '', UrlUtil.removeURLParameter(window.location.href, 'search'))
      return
    }

    history.pushState(
      null,
      '',
      UrlUtil.updateURLParameter(
        window.location.href,
        'search',
        urlFormatSearchKeyword(e.target.value)
      )
    )

    if (e.target.value.length >= 3) {
      setQueryString(window.location.search)
      handlePaginationClick(1, true)
    }
  }

  const clearSearch = () => {
    setSearchText('')
    setQueryString(queryStringFilterSet)
    if (searchText) {
      handlePaginationClick(1, true)
      history.pushState(null, '', UrlUtil.removeURLParameter(window.location.href, 'search'))
    }
  }

  const handleSearchClick = (e: any) => {
    setShowSearch(!showSearch)
  }

  // Only control the current page in one location NO STATE
  const getPageNumber = () => {
    if (typeof window !== 'undefined' && window.location.pathname) {
      const numberInUrl = window.location.pathname.match(/[0-9]+/)
      if (numberInUrl && numberInUrl[0]) {
        return parseInt(numberInUrl[0])
      }
    } else {
      if (pageNumber) {
        return pageNumber
      }
    }
    return 1
  }

  const hrefForPage = (page: number) => {
    let url = ''
    const hasFilters = queryString && queryString !== null && queryString !== ''

    if (page === 1) {
      // url = /resources/all
      if (typeof window !== 'undefined') {
        if (hasFilters) {
          url = `${defaultSlug}${queryString}`
        } else {
          url = `${defaultSlug}`
        }
      } else if (defaultSlug) {
        // SSR
        url = `${defaultSlug}`
      }
    } else {
      // url = /resources/all/page?[query]
      if (typeof window !== 'undefined') {
        if (hasFilters) {
          url = `${defaultSlug}/${page}${queryString}`
        } else {
          url = `${defaultSlug}/${page}`
        }
      } else if (defaultSlug) {
        url = `${defaultSlug}/${page}`
      }
    }
    return url
  }

  const currentPage = getPageNumber()
  const start = (currentPage - 1) * resourcesPerPage
  const end = currentPage * resourcesPerPage

  const filteredResources = () => {
    //this means we haven't run init yet (like when doing the html rendering during createPage rendering)
    if (!listingData || listingData.length == 0) {
      setListingData(resourceData)
    }

    let filteredResourceData = [...listingData]

    // filter by body or title
    if (searchText.length >= 3)
      filteredResourceData = filteredResourceData.filter(
        (resource: any) =>
          resource.body?.includes(searchText.toLowerCase()) ||
          resource.title?.toLowerCase().includes(searchText.toLowerCase())
      )

    let set = new Set()
    checkboxFilters.forEach((item: any) => {
      set.add(item.typename)
    })

    let filterMap: any = {}

    set.forEach((item: any) => {
      filterMap[item] = new Set()
    })

    for (let i = 0; i < filtersAdded.length; i++) {
      const filterGroup = filtersAdded[i]
      const filterValues: any = []

      // Ensure there is active filters for the filter group
      if (filterGroup.filters.length !== 0) {
        // Filter resources within a group by OR
        let filterTypeName = ''
        const groupFilterItems = filterGroup.filters
        for (let i = 0; i < groupFilterItems.length; i++) {
          filterValues.push(groupFilterItems[i].value)
          // typename will always be the same within a filter group
          filterTypeName = groupFilterItems[i].typename
        }

        filteredResourceData = filteredResourceData.filter((resource: any) => {
          if (resource[filterTypeName]) {
            return filterValues.some((item: any) => resource[filterTypeName].includes(item))
          } else {
            return false
          }
        })
      }
    }

    let topic: string
    let value: any

    filteredResourceData.forEach((resource: any) => {
      for ([topic, value] of Object.entries(filterMap)) {
        if (resource[topic]) {
          resource[topic].forEach((item: any) => {
            value.add(item)
          })
        }
      }
    })

    return [filteredResourceData, filterMap]
  }

  if (pageData && pageData.flexSection) {
    const resourcePageModules = pageData.flexSection
    dynamicPageContent = resourcePageModules.map((module: any, i: Number) => {
      switch (module.__typename) {
        case ModuleNames.HERO:
          const pageTitle = pageData.pageName ? pageData.pageName : ''
          return (
            <Hero
              data={module}
              location={location}
              pageTitle={pageTitle}
              showBreadcrumbs={true}
              key={`page-component--${i}`}
              pageType={'resourceListing'}
              metaInformation={pageData.metaInformation}
              pageUseCase={pageData.useCase}
              locale={defaultLocale}
              breadcrumb={pageData.breadcrumb}
            />
          )
        case ModuleNames.GLOBAL_CONTENT_BLOCK:
          if (useCase === 'Events') {
            module.contentBlockTheme = 'Dark'
            module.cta1Style = 'primary'
            eventsContentBlockHeadline = module.headline
          } else {
            module.cta1Style = 'Link'
            module.contentBlockTheme = 'Light'
          }
          eventPageContentBlock = (
            <ContentBlock data={module} key={`page-component--${i}`}></ContentBlock>
          )
          return ''

        case ModuleNames.RESOURCE_ROW:
          if (isEvents) {
            eventPageResourceRow = (
              <ResourceRow
                data={module}
                key={`page-component--${i}`}
                locale={defaultLocale}></ResourceRow>
            )
          }
          return ''
      }
    })
  }

  const addSearchQuery = (queryString: string, searchQuery: string) => {
    if (searchQuery.length >= 3) {
      return queryString
        ? `${queryString}&search=${urlFormatSearchKeyword(searchQuery)}`
        : `?search=${urlFormatSearchKeyword(searchQuery)}`
    }
    return queryString
  }

  const handleFilterItemClick = (e?: any, multiFilterArr?: any, newQueryString?: string) => {
    let filterGroupKebab = ''
    let filterValue = ''
    let newFilterArr = [...checkboxFilters]
    let newFiltersAddedArr = [...filtersAdded]

    // set multiple filters at once
    if (multiFilterArr && newQueryString) {
      for (let x = 0; x < multiFilterArr.length; x++) {
        const filterIndex = multiFilterArr[x].checkboxIndex
        const updatedFilter = multiFilterArr[x].checkboxData
        const filterGroupName = multiFilterArr[x].checkboxData.filterGroup

        newFilterArr[filterIndex] = updatedFilter
        for (let i = 0; i < newFiltersAddedArr.length; i++) {
          const filterAddedGroup = newFiltersAddedArr[i]

          // If there is a filter group active, add to that existing group's selected filters array
          if (filterAddedGroup?.name === filterGroupName) {
            filterAddedGroup.filters.push(updatedFilter)
          }
        }
      }
      setCheckboxFilters(newFilterArr)
      setFiltersAdded(newFiltersAddedArr)
      setQueryString(addSearchQuery(newQueryString, searchText))
    } else {
      if (e?.target?.getAttribute('data-item')) {
        const filterGroupName = e.target.getAttribute('data-item')
        filterGroupKebab = kebabCaseString(filterGroupName)

        if (e?.target?.value) {
          filterValue = e.target.value

          if (e?.target?.getAttribute('data-idx')) {
            const filterIndex = e?.target?.getAttribute('data-idx')

            let newQueryString =
              queryStringFilterSet && queryStringFilterSet !== '?filterResources='
                ? queryStringFilterSet
                : '?filterResources='

            // queryStringFilterSet
            let newQueryStringItem = `&${filterGroupKebab}=${filterValue}`
            let updatedFilter = { ...newFilterArr[filterIndex] }

            const fieldUnchecked = !updatedFilter.checked === false

            // If a field is unchecked, we need to remove the filter from the query string
            if (fieldUnchecked) {
              newQueryString = newQueryString.replace(newQueryStringItem, '')
              // Remove query string if no filters are applied
              if (newQueryString === '?filterResources=') {
                newQueryString = ''
              }
            } else {
              newQueryString += newQueryStringItem
            }

            updatedFilter.checked = !updatedFilter.checked

            for (let i = 0; i < newFiltersAddedArr.length; i++) {
              const filterAddedGroup = newFiltersAddedArr[i]

              // If there is a filter group active, add to that existing group's selected filters array
              if (filterAddedGroup?.name === filterGroupName) {
                if (filterAddedGroup.filters.length === 0) {
                  filterAddedGroup.filters.push(updatedFilter)
                } else {
                  if (fieldUnchecked) {
                    for (let k = 0; k < filterAddedGroup.filters.length; k++) {
                      // if item already exists it should be removed
                      if (filterAddedGroup.filters[k].key === updatedFilter.key) {
                        filterAddedGroup.filters.splice(k, 1)
                      }
                    }
                  } else {
                    filterAddedGroup.filters.push(updatedFilter)
                  }
                }
              }
            }

            newFilterArr[filterIndex] = updatedFilter
            setCheckboxFilters(newFilterArr)
            setFiltersAdded(newFiltersAddedArr)
            setQueryStringFilterSet(newQueryString)
            setQueryString(addSearchQuery(newQueryString, searchText))
            handlePaginationClick(1, true)
          }
        }
      }
    }
  }

  const toggleDrawer = (selectedIdx: number, toggleDirection: string) => {
    // For drawer of secondary filters
    if (filterDrawers?.current) {
      let topicTitle: string
      let el: any
      let idx = 0
      let selectedTopic = ''

      for ([topicTitle, el] of Object.entries(filterDrawers.current)) {
        if (idx === selectedIdx) {
          selectedTopic = topicTitle
        }
        if (filterDrawers?.current[topicTitle]?.classList.contains('Secondary-Drawer__Active')) {
          filterDrawers.current[topicTitle].classList.remove('Secondary-Drawer__Active')
        }
        idx++
      }
      if (toggleDirection === 'open') {
        // Close any open drawers before opening another!
        filterDrawers?.current[selectedTopic]?.classList.add('Secondary-Drawer__Active')
      }
    }
  }

  const resetFilterState = (isInitialization?: boolean) => {
    let filterState: any = []
    let filterGroupState: any = []

    const filterData = pageData?.filters ? pageData.filters : []
    if (filterData && filterData?.length !== 0) {
      for (let i = 0; i < filterData.length; i++) {
        const filterGroup = filterData[i]
        const filterGroupName = filterData[i].name ? filterData[i].name : ''
        filterGroupState.push({
          name: filterGroupName,
          filters: [],
        })
        if (filterGroup.items && filterGroup.items.length !== 0) {
          for (let k = 0; k < filterGroup.items.length; k++) {
            let filterItemName
            const item = filterGroup.items[k]
            if (item?.title) {
              filterItemName = item.title
            } else if (item?.customerName) {
              filterItemName = item.customerName
            }
            const filterTypeName = item.__typename ? item.__typename : ''
            const strippedValue = kebabCaseString(filterItemName)

            filterState.push({
              key: `${strippedValue}`,
              checked: false,
              value: filterItemName,
              typename: filterTypeName,
              filterGroup: filterGroupName,
            })
          }
        }
      }

      setCheckboxFilters(filterState)
      setFiltersAdded(filterGroupState)
      if (!isInitialization) {
        setQueryStringFilterSet('')
        clearSearch()
        setQueryString('')
      }
    }
  }

  const updateURL = () => {
    if (queryString !== null) {
      if (typeof window !== `undefined`) {
        if (queryString === '') {
          history.pushState(null, '', defaultSlug)
        } else {
          history.pushState(null, '', queryString)
        }
      }
    }
  }

  const init = () => {
    if (checkboxFilters && checkboxFilters.length === 0) {
      resetFilterState(true)
    }

    if (!listingData || listingData.length == 0) {
      setListingData(resourceData)
    }

    // If there is a query string, we need to trigger the filters
    // Only need to do this on initial render
    if (
      typeof window !== `undefined` &&
      checkboxFilters &&
      checkboxFilters.length !== 0 &&
      queryString === null
    ) {
      let checkboxesToSet = []
      if (window.location.href.includes('?filterResources=')) {
        const existingQueryString = window.location.href.split('?filterResources')
        let urlFiltersToApply = window.location.href.split('&')
        urlFiltersToApply.shift()

        if (urlFiltersToApply && urlFiltersToApply.length !== 0) {
          for (let i = 0; i < checkboxFilters.length; i++) {
            const checkbox = { ...checkboxFilters[i] }
            for (let k = 0; k < urlFiltersToApply.length; k++) {
              const filter = urlFiltersToApply[k]
              const filterElements = filter.split('=')
              const filterGroupName = filterElements[0]
              const filterKey = filterElements[1]

              if (kebabCaseString(checkbox.filterGroup) === filterGroupName) {
                if (checkbox.key === filterKey) {
                  // Matching filter with parameter

                  checkbox.checked = true

                  const checkBoxFilterInfo = {
                    checkboxIndex: i,
                    checkboxData: checkbox,
                  }

                  checkboxesToSet.push(checkBoxFilterInfo)
                }
              }
            }
          }

          let newQueryString =
            existingQueryString && existingQueryString[1]
              ? `?filterResources${existingQueryString[1]}`
              : ''

          // remove search param if it exists
          newQueryString = newQueryString
            .split('&')
            .filter(val => !val.includes('search='))
            .join('&')
          setQueryStringFilterSet(newQueryString)
          handleFilterItemClick('', checkboxesToSet, newQueryString)
        }
      } else if (window.location.href.includes('search=')) {
        const searchParam = window.location.href.split('search=')[1]
        addSearchQuery(queryStringFilterSet, searchParam)
      }
    }
  }

  useEffect(() => {
    let searchValue = UrlUtil.getUrlParameter('search').replace(/-/g, ' ')
    // Initial page load, set search text if it exist to trigger resource filtering

    if (searchValue && searchText != searchValue) {
      setSearchText(searchValue)
      setShowSearch(true)
    }
  }, [])

  useEffect(() => {
    init()
  }, [checkboxFilters])

  useEffect(() => {
    if (filtersAdded && queryString && queryString !== '' && searchText.length >= 3) {
      filteredResources()
    }
  }, [filtersAdded, searchText])

  useEffect(() => {
    updateURL()
  }, [queryString])

  const renderFilters = (filtersToShow: any, numResults: number) => {
    const filterData = pageData?.filters ? pageData.filters : []
    let checkedFilters: any = []
    let filterStateIdx: number = 0

    const showMoreText = findTarget('ShowMore.text', microcopy.values, false)
    const showLessText = findTarget('ShowLess.text', microcopy.values, false)
    const filterByText = findTarget('Filter.text', microcopy.values, false)
    const activeFiltersText = findTarget('ActiveFilter.text', microcopy.values, false)
    const clearFiltersText = findTarget('CloseFilter.text', microcopy.values, false)
    const applyFiltersText = findTarget('ApplyFilters.text', microcopy.values, false)

    if (filterData && filterData?.length !== 0) {
      const filtersToDisplay = filterData.map((filterGroup: any, n: number) => {
        const filterName = filterGroup.name ? filterGroup.name : ''
        const typeName = filterGroup.items.length && filterGroup.items[0]?.__typename
        let primaryItems: any = []
        let secondaryItems: any = []
        if (filterGroup.items && filterGroup.items.length !== 0) {
          let filtered = filterGroup.items
          for (let k = 0; k < filtered.length; k++) {
            if (filtered[k]) {
              const item = filtered[k]
              let filterItemName: any
              if (item?.title) {
                filterItemName = item.title
              } else if (item?.customerName) {
                filterItemName = item.customerName
              }
              const strippedValue = kebabCaseString(filterItemName)

              const isChecked =
                checkboxFilters &&
                checkboxFilters[filterStateIdx] &&
                checkboxFilters[filterStateIdx].checked

              const filterElement = (
                <Checkbox htmlFor={filterItemName} key={`Filter-Item--${filterItemName}`}>
                  <input
                    id={filterItemName}
                    type="checkbox"
                    value={strippedValue}
                    className="Checkbox__Input"
                    data-item={filterName}
                    data-idx={filterStateIdx}
                    data-group-idx={n}
                    onClick={handleFilterItemClick}
                    checked={isChecked}
                  />
                  <Typography type="body" className="Checkbox__Label">
                    {filterItemName}
                  </Typography>
                </Checkbox>
              )
              if (isChecked) {
                checkedFilters.push(filterElement)
              }

              if (
                (filtersToShow && filtersToShow[typeName]?.has(filtered[k].title)) ||
                !filtersToShow
              ) {
                if (primaryItems.length <= 3) {
                  primaryItems.push(filterElement)
                } else {
                  secondaryItems.push(filterElement)
                }
              }
            }
            filterStateIdx++
          }
        }

        if (primaryItems.length) {
          return (
            <FilterGroup
              key={`Filter-Group--${n}`}
              ref={drawer => {
                filterDrawers.current[filterName] = drawer
              }}>
              <Typography type="header6" className="Filter-Name">
                {filterName}
              </Typography>
              {primaryItems}
              {secondaryItems && secondaryItems.length !== 0 && (
                <>
                  <div className="Filter-Group__Secondary-Items">{secondaryItems}</div>
                  <ToggleText className={`Toggle-Text--${n}`} useCase={useCase}>
                    <button
                      onClick={() => toggleDrawer(n, 'close')}
                      className="Toggle-Text__Button Toggle-Text__Button--Less">
                      <BlueArrowDown isEvents={isEvents} />
                      <Typography
                        className="Toggle-Text__Label Toggle-Text__Label--Less"
                        type="bodyXS">
                        {showLessText}
                      </Typography>
                    </button>
                    <button
                      onClick={() => toggleDrawer(n, 'open')}
                      className="Toggle-Text__Button Toggle-Text__Button--More">
                      <BlueArrowDown isEvents={isEvents} />
                      <Typography
                        className="Toggle-Text__Label Toggle-Text__Label--More"
                        type="bodyXS">
                        {showMoreText}
                      </Typography>
                    </button>
                  </ToggleText>
                </>
              )}
            </FilterGroup>
          )
        }
      })

      return (
        <ResourceListingLeftFilterContainer
          useCase={useCase}
          mobileDrawerOpen={mobileDrawerOpen}
          defaultLocale={defaultLocale}
          displayFilters={hasFiltersSelected() || numResults > 0}>
          <div>
            <div className="ResourceListing__Filters-Mobile-Actions">
              <Button
                icon={false}
                clickHandler={handleDrawerOpen}
                text={applyFiltersText}
                type={'primary'}
                size={'small'}
                theme={'light'}
              />

              <button onClick={handleDrawerOpen}>
                <WrapperImage
                  src={
                    isEvents
                      ? ImgUtil.getResizedImageUrl(
                          'https://images.ctfassets.net/5965pury2lcm/1JXOngMrnkhHQvWgegGKIy/22d441351e0b349d6c55dae407ec09e4/X-Icon-White.png',
                          ''
                        )
                      : ImgUtil.getResizedImageUrl(
                          'https://images.ctfassets.net/5965pury2lcm/3BIcaw9GtzKFLmNtPhGLCo/15d3a016de92a9a44581692940c64c92/Error.png',
                          ''
                        )
                  }
                  alt="Close icon"
                  className="ResourceListing__Filters-Close-Icon"
                  width={48}
                  height={48}
                  threshold={650}
                />
              </button>
            </div>
            <div className="ResourceListing__Filters-Inner-Container">
              {checkedFilters && checkedFilters.length > 0 && (
                <>
                  <Typography type="captionAlt" className="Filter-By__Label">
                    {activeFiltersText}
                  </Typography>
                  {checkedFilters}
                  <ToggleText
                    className="Toggle-Text--Margin-Bottom"
                    useCase={useCase}
                    onClick={() => {
                      resetFilterState(false)
                    }}>
                    <RedX />
                    <Typography className="Toggle-Text__Label-Black" type="bodyXS">
                      {clearFiltersText}
                    </Typography>
                  </ToggleText>
                </>
              )}
              <div className={numResults === 0 ? 'Filters--hide' : ''}>
                <Typography type="captionAlt" className="Filter-By__Label">
                  {filterByText}
                </Typography>
                {filtersToDisplay}
              </div>

              <Typography type="header6" className="Filter-Name">
                {eventsContentBlockHeadline}
              </Typography>
              {eventPageContentBlock}
            </div>
            {defaultLocale !== 'en-US' && wantMore && allResources && (
              <MoreResourcesContainer>
                <Typography
                  type="bodyXS"
                  className={`ResourceListing__Filters-Want-More-Text--${
                    isEvents ? 'dark' : 'light'
                  }`}>
                  {wantMore}
                </Typography>
                <Button
                  type="text-link"
                  theme={isEvents ? 'dark' : 'light'}
                  text={allResources}
                  icon={true}
                  href={`/resources/all`}
                />
              </MoreResourcesContainer>
            )}
          </div>
        </ResourceListingLeftFilterContainer>
      )
    }
  }

  const hasFiltersSelected = () => {
    return filtersAdded.some((topics: any) => topics?.filters?.length)
  }

  const microcopyFeaturedText = findTarget(
    'FeaturedPILL.text',
    pageContext.microcopySetResources?.values || [],
    false
  )

  const onDemandFeaturedText = findTarget(
    'resource.7.OnDemandPILL.text',
    pageContext.microcopySetResources?.values || [],
    false
  )

  const inEnglishText =
    findTarget('InEnglishPILL.text', pageContext.microcopySetResources?.values || [], false) ||
    'In English'

  const [resourcesToShow, filtersToShow] = filteredResources()

  const noResultsSearch = findTarget(`NoResultsSearch.text`, microcopy.values, false)

  const suggestedSearch1 = findTarget(`SuggestedSearch1.text`, microcopy.values, false)
  const suggestedSearch2 = findTarget(`SuggestedSearch2.text`, microcopy.values, false)

  const suggestedRow = findTarget(`SuggestedRow.text`, microcopy.values, false)

  const suggestedSearchFilter1 = findTarget(`SuggestedSearchFilter1.text`, microcopy.values, false)

  const searchResults = findTarget(`SearchResults.text`, microcopy.values, false)

  const noResultsFilter = findTarget(`NoResultsFilter.text`, microcopy.values, false)

  const suggestedFilter = findTarget(`SuggestedFilter.text`, microcopy.values, false)

  const clearFilter = findTarget(`ClearFilter.text`, microcopy.values, false)

  const setSingleFilterResults = (id: string) => {
    let filter
    if (checkboxFilters) {
      filter = checkboxFilters.find((f: any) => {
        return f.value === id
      })
    }

    let filters = [...checkboxFilters]
    let filtersChecked: any = [...filtersAdded]
    if (filter) {
      filters = filters.map((f: any) => {
        if (f.value === id) {
          f.checked = true
        } else f.checked = false
        return f
      })

      for (let i = 0; i < filtersChecked.length; i++) {
        if (filtersChecked[i].name === filter.filterGroup) filtersChecked[i].filters = [filter]
        else filtersChecked[i].filters = []
      }
    }
    setCheckboxFilters(filters)
    setFiltersAdded(filtersChecked)
    clearSearch()

    setQueryStringFilterSet(`?filterResources=&${filter.filterGroup}=${filter.key}`)
    setQueryString(`?filterResources=&${filter.filterGroup}=${filter.key}`)
  }

  const topThreeFilters = pageContext.topThreeFilters || []
  const topThreeFilterText = topThreeFilters.map((filter: any, idx: number) => {
    let seperator = ''
    if (idx === 0) seperator = ', '
    else if (idx === 1) seperator = ' or '
    return (
      <a key={`TopThreeFilter-${filter}`} onClick={() => setSingleFilterResults(filter)}>
        {filter}
        <span>{seperator}</span>
      </a>
    )
  })

  let searchResultsText = ``
  let extraText: any
  if (searchText.length >= 3) {
    if (resourcesToShow.length > 0) searchResultsText = `${searchResults} "${searchText}"`
    else {
      searchResultsText = `${noResultsSearch} "${searchText}"`
      if (hasFiltersSelected()) {
        extraText = (
          <>
            {suggestedSearchFilter1}{' '}
            <a
              onClick={() => {
                resetFilterState(false)
              }}>
              {clearFilter}
            </a>
            <br />
            {suggestedSearch2} {topThreeFilterText}
          </>
        )
      } else {
        extraText = (
          <>
            {suggestedSearch1}
            <br />
            {suggestedSearch2} {topThreeFilterText}
          </>
        )
      }
    }
  } else if (hasFiltersSelected() && resourcesToShow.length === 0) {
    searchResultsText = `${noResultsFilter}`
    extraText = (
      <>
        {suggestedFilter}{' '}
        <a
          onClick={() => {
            resetFilterState(false)
          }}>
          {clearFilter}
        </a>
      </>
    )
  }

  const handleDrawerOpen = () => {
    // Bring to top of menu if it's not open
    if (mobileDrawerOpen === false && typeof window !== undefined) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    setMobileDrawerOpen(!mobileDrawerOpen)
  }

  let trending, numOfColumns
  let maxTrending = Math.min(featuredResources.length, 4)
  if (resourcesToShow.length === 0 && maxTrending) {
    let random = RandomizerUtil.getRandomItemsFromArray(featuredResources, maxTrending)

    trending = random.map((featured: any, i: number) => {
      return ResourceCard(
        featured,
        i,
        isEvents,
        microcopyFeaturedText,
        onDemandFeaturedText,
        inEnglishText,
        defaultLocale
      )
    })

    numOfColumns = !hasFiltersSelected() && resourcesToShow.length === 0 ? 4 : 3
  }

  const resourceListingSection = (
    <ResourceListingBottomContainer>
      {renderFilters(
        !hasFiltersSelected() && searchText.length <= 3 ? null : filtersToShow,
        resourcesToShow.length
      )}
      <div>
        <SearchResultsDescription isEvents={isEvents} className={searchResultsText ? `active` : ''}>
          <Typography type="header5">{searchResultsText}</Typography>
          {extraText && <Typography type="bodyXS">{extraText}</Typography>}
        </SearchResultsDescription>
        {resourcesToShow.length > 0 && (
          <>
            <ResourceListingItemContainer>
              {resourcesToShow.slice(start, end).map((resource: any, i: number) => {
                return ResourceCard(
                  resource,
                  i,
                  isEvents,
                  microcopyFeaturedText,
                  onDemandFeaturedText,
                  inEnglishText
                )
              })}
            </ResourceListingItemContainer>
            <PaginationContainer>
              {resourcesToShow && resourcesToShow.length > resourcesPerPage && (
                <Pagination
                  activePage={currentPage}
                  hrefForPage={hrefForPage}
                  handleClick={handlePaginationClick}
                  numResults={resourcesToShow.length}
                  numPagesBeforeEllipses={3}
                  theme={isEvents ? 'dark' : 'light'}
                  numResultsPerPage={resourcesPerPage}
                />
              )}
            </PaginationContainer>
          </>
        )}
        {trending && (
          <TrendingResourcesContainer>
            {suggestedRow && <Typography type="label">{suggestedRow}</Typography>}
            <ResourceListingItemContainer columns={numOfColumns}>
              {trending.slice(0, numOfColumns)}
            </ResourceListingItemContainer>
          </TrendingResourcesContainer>
        )}
      </div>
    </ResourceListingBottomContainer>
  )

  const urlIsMatch = (url: string) => {
    try {
      let convertedURL = url.indexOf('http') !== 0 ? new URL(url, location.origin) : new URL(url)
      return convertedURL.pathname === location.pathname
    } catch (err) {
      return false
    }
  }

  const resourceListingBodySection = (
    <GlobalContainer>
      <ResourceListingTopFilterContainer isEvents={isEvents} id="ResourceListingTopFilterContainer">
        {/* Uncomment once search is implemented */}

        <SearchContainer>
          <InputContainer className={`${showSearch ? 'active' : ''}`}>
            <input
              type="input"
              placeholder="Search for..."
              value={searchText}
              onChange={handleSearchInput}
              ref={searchInputRef}
            />
            <svg
              width="16"
              height="16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={clearSearch}
              className={`close-icon ${searchText.length ? 'active' : ''}`}>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="m8.99 8 4.505-4.505a.7.7 0 0 0-.99-.99L8 7.01 3.495 2.505a.7.7 0 0 0-.99.99L7.01 8l-4.505 4.505a.7.7 0 1 0 .99.99L8 8.99l4.505 4.505a.7.7 0 0 0 .99-.99L8.99 8Z"
                fill="#000"
              />
            </svg>
          </InputContainer>
          <SearchIconContainer isEvents={isEvents} onClick={handleSearchClick}>
            <svg
              width="16"
              height="16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="search-icon">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="m10.741 10.464 3.803 3.802a.67.67 0 1 1-.948.948L9.794 11.41A6.03 6.03 0 1 1 6.03.67a6.03 6.03 0 0 1 4.711 9.794Zm-4.711.926a4.69 4.69 0 1 0 0-9.38 4.69 4.69 0 0 0 0 9.38Z"
                fill="#fff"
              />
              <mask
                id="a"
                mask-type="alpha"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="15"
                height="16">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="m10.741 10.464 3.803 3.802a.67.67 0 1 1-.948.948L9.794 11.41A6.03 6.03 0 1 1 6.03.67a6.03 6.03 0 0 1 4.711 9.794Zm-4.711.926a4.69 4.69 0 1 0 0-9.38 4.69 4.69 0 0 0 0 9.38Z"
                  fill="#fff"
                />
              </mask>
            </svg>
          </SearchIconContainer>
        </SearchContainer>

        <DropDown
          icon={true}
          items={centerTabs}
          handleFilterButtonClick={handleDrawerOpen}
          isEvents={isEvents}
        />

        <div className="Tab__desktop_buttons">
          {centerTabs.map((tab: any, i: number) => {
            const link = tab.internalLink ? internalLinkHandler(tab.internalLink) : tab.externalUrl
            return (
              <Button
                key={`Resource-Center-Tab__${i}`}
                icon={false}
                href={link}
                text={tab.text}
                type="secondary"
                size="medium"
                theme="dark"
                className={`ResourceCenter__Tab__BTNS ${
                  urlIsMatch(link) ? 'ResourceCenter__Tab__BTNS__active' : ''
                }`}
              />
            )
          })}
        </div>
      </ResourceListingTopFilterContainer>
      {eventPageResourceRow}
      {resourceListingSection}
    </GlobalContainer>
  )

  const pageInner = (
    <React.Fragment>
      <SEOTags
        seoMetadata={seoData}
        path={path}
        slug={defaultSlug}
        locale={defaultLocale}
        isPaginated={true}
        pageNum={currentPage}
        isLastPage={currentPage === Math.ceil(resourceData.length / resourcesPerPage)}
        hrefForPage={hrefForPage}
        availableLocales={pageContext.availableLocales}
      />
      <ResourcePageOuter useCase={useCase} data-cy="ListingPage">
        {dynamicPageContent}
        <ResourceListingContainer useCase={useCase}>
          {resourceListingBodySection}
        </ResourceListingContainer>
      </ResourcePageOuter>
    </React.Fragment>
  )

  return (
    <Layout.Template
      lang={defaultLocale}
      pageType="resourceListing"
      slug={defaultSlug}
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}
      availableLocales={pageContext.availableLocales}>
      <div data-cy={`ResourceCenterLandingPage-${useCase}`}>{pageInner}</div>
    </Layout.Template>
  )
}

export default ResourceCenterLandingPage
